import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from './ui/accordion';
import { HelpCircle, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FAQList = () => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([axios.get(`${API}/faqs`), axios.get(`${API}/faq-categories`)])
      .then(([f, c]) => { setFaqs(f.data || []); setCategories(c.data || []); })
      .catch(() => toast.error('Failed to load FAQ'))
      .finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const visible = q
    ? faqs.filter((f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q))
    : faqs;

  const sections = [
    ...categories.map((c) => ({ id: c.id, name: c.name, items: visible.filter((f) => f.category_id === c.id) })),
    { id: 'general', name: 'General', items: visible.filter((f) => !f.category_id || !categories.some((c) => c.id === f.category_id)) },
  ].filter((s) => s.items.length > 0);

  return (
    <div className="space-y-4">
      <div className="border-b border-border pb-4 flex items-center gap-3">
        <HelpCircle className="w-6 h-6 text-primary" strokeWidth={1.5} />
        <div>
          <h1 className="text-2xl font-barlow tracking-widest text-foreground">FAQ</h1>
          <p className="text-xs text-muted-foreground mt-1">Frequently asked questions.</p>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search questions…"
          data-testid="faq-search-input"
          className="pl-9 rounded-sm"
        />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : sections.length === 0 ? (
        <Card className="rounded-sm border-border">
          <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="faq-empty">
            {q ? 'No questions match your search.' : 'No questions yet. Check back later.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.id} className="space-y-2" data-testid={`faq-section-${section.id}`}>
              <h2 className="text-sm font-barlow tracking-widest uppercase text-primary border-l-2 border-primary pl-3">
                {section.name}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {section.items.map((f) => (
                  <AccordionItem key={f.id} value={f.id} className="border border-border rounded-sm px-4 bg-card">
                    <AccordionTrigger className="text-sm font-medium text-left hover:no-underline hover:text-primary" data-testid={`faq-question-${f.id}`}>
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {f.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
